import type { ValuesType } from 'utility-types'

import { assert, objectEntries } from '../../../../../../utils'

import {
    areDetailedCybusEndpointEquals,
    Capability,
    ConnectwareError,
    ConnectwareErrorType,
    type CybusEndpointAddress,
    type StatusType,
} from '../../../../../../domain'

import type { SingleSubscriptionsTypes } from '../../../../../../application'

import { type Resource, TopicsMapper } from '../../../../mappers'
import { BaseSingleResourceSubscriptionStrategy, type SingleResourceBaseRequest } from '../Resource'
import { mapBaseEndpoint } from './Base'

type EndpointResourceConfigArgs = Resource<'Endpoint'>['resourceConfig']['args']

const isExpectedAddressValueType = (value: ValuesType<EndpointResourceConfigArgs['address']>): value is ValuesType<CybusEndpointAddress> =>
    Boolean(
        typeof value === 'number' ||
            typeof value === 'boolean' ||
            typeof value === 'string' ||
            (value && typeof value === 'object' && Object.values(value).every((v) => typeof v === 'string'))
    )

const isExpectedAddressType = (address: EndpointResourceConfigArgs['address']): address is CybusEndpointAddress =>
    typeof address === 'object' && objectEntries(address).every(([key, value]) => typeof key === 'string' && isExpectedAddressValueType(value))

const mapToAddress = (address: Resource<'Endpoint'>['resourceConfig']['args']['address']): CybusEndpointAddress => {
    assert(isExpectedAddressType(address), new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Unexpected address type', address))
    return address
}

export class DetailedEndpointStrategy extends BaseSingleResourceSubscriptionStrategy<'Endpoint', 'endpoint'> {
    protected override readonly areEquals = areDetailedCybusEndpointEquals

    protected override resourceCategory = 'Endpoint' as const

    protected readonly notFoundMessage = 'Could not fetch endpoint'

    protected readonly resourceStatusType = 'endpoint' as const

    protected readonly baseRequest: SingleResourceBaseRequest<'Endpoint'> = {
        capability: Capability.ENDPOINT_READ,
        method: 'GET',
        path: '/api/v2/resources/+',
    }

    override readonly requiredPermissions = [Capability.ENDPOINT_READ]

    // eslint-disable-next-line class-methods-use-this
    protected mapResource (endpoint: Resource<'Endpoint'>, status: StatusType): SingleSubscriptionsTypes['endpoint'] {
        return {
            ...mapBaseEndpoint(endpoint),
            address: mapToAddress(endpoint.resourceConfig.args.address),
            rules: endpoint.resourceConfig.args.rules || null,
            topics: TopicsMapper.create(endpoint.category).withOperation(endpoint.resourceConfig.args.operation).mapAll(endpoint.resourceConfig.args.topic),
            status,
        }
    }
}
